import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_API_URL}/house`;

export interface House {
  id?: number;
  houseName?: string;
  houseCode?: string;
  houseColor?: string;
  createdAt?: string;
  updatedAt?: string;
}

const getAllHouse = async (): Promise<House[]> => {
  try {
    const resp = await axios.get(API_URL);
    return resp.data;
  } catch (error: any) {
    if (error?.response?.status === 404 || error?.response?.status === 500) {
      return [];
    }
    throw error;
  }
};

const addHouse = async (house: House): Promise<House> => {
  const resp = await axios.post(API_URL, house);
  return resp.data;
};

const updateHouse = async (house: House): Promise<House> => {
  const resp = await axios.put(API_URL, house);
  return resp.data;
};

const deleteHouse = async (id: number): Promise<void> => {
  await axios.delete(`${API_URL}/${id}`);
};

export function useHouse() {
  const queryClient = useQueryClient();

  const getAllHouseQuery = useQuery<House[], Error>({
    queryKey: ["house"],
    queryFn: getAllHouse,
  });

  const addHouseMutation = useMutation<House, Error, House>({
    mutationFn: addHouse,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["house"] });
    },
  });

  const updateHouseMutation = useMutation<House, Error, House>({
    mutationFn: updateHouse,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["house"] });
    },
  });

  const deleteHouseMutation = useMutation<void, Error, number>({
    mutationFn: deleteHouse,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["house"] });
    },
  });

  return {
    getAllHouseQuery,
    addHouseMutation,
    updateHouseMutation,
    deleteHouseMutation,
  };
}
